<!--
  AgreementCheckbox:AMOE 协议勾选 + 内联 TOS / Privacy 链接
  - props: checked / pageType
  - emits:
    - update:checked(boolean)   勾选状态变化
    - agreement:tos-click()      点击 Terms of Service 文本
    - agreement:privacy-click()  点击 Privacy Policy 文本
  - D-19 + 硬约束:组件绝不直接触发外部跳转,跳转完全由宿主决定
  - 勾选态背景 #F36F12(Figma 锁死 accent 例外,带 rationale 见 UI-SPEC)
  - 文案模板来自 i18n amoe.form.agreement.label,含 {tos} {privacy} 占位
-->
<template>
  <div
    class="agreement"
    :class="{ isPC: pageType === 'PC' }"
  >
    <label class="checkbox-wrapper">
      <input
        type="checkbox"
        class="native-cb"
        :checked="checked"
        @change="onToggle"
      />
      <span
        class="checkbox"
        :class="{ checked }"
        aria-hidden="true"
      >
        <svg
          v-if="checked"
          viewBox="0 0 20 20"
          class="check-icon"
        >
          <path
            d="M4 10 L8 14 L16 6"
            fill="none"
            stroke="#FFFFFF"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </label>
    <span class="text">{{ prefix }}<span
      class="link"
      role="button"
      tabindex="0"
      @click.stop="onTos"
      @keydown.enter.stop="onTos"
      @keydown.space.stop.prevent="onTos"
    >{{ tosText }}</span>{{ middle }}<span
      class="link"
      role="button"
      tabindex="0"
      @click.stop="onPrivacy"
      @keydown.enter.stop="onPrivacy"
      @keydown.space.stop.prevent="onPrivacy"
    >{{ privacyText }}</span>{{ suffix }}</span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue-demi';
import t from '../render/utils/i18n';

export default defineComponent({
  name: 'AgreementCheckbox',
  props: {
    checked: { type: Boolean, default: false },
    pageType: { type: String as () => 'H5' | 'PC', required: true },
  },
  emits: ['update:checked', 'agreement:tos-click', 'agreement:privacy-click'],
  setup(_, { emit }) {
    const raw = computed<string>(() => t('amoe.form.agreement.label'));
    const tosText = computed<string>(() => t('amoe.form.agreement.tos'));
    const privacyText = computed<string>(() => t('amoe.form.agreement.privacy'));

    // 模板形如 'I agree to the {tos} and {privacy}.'
    // 拆成 prefix / middle / suffix,保证即便 i18n 缺失也有兜底
    const segments = computed<{ prefix: string; middle: string; suffix: string }>(() => {
      const s = raw.value;
      const [beforeTos, afterTos] = s.split('{tos}');
      const [middlePart, afterPrivacy] = (afterTos ?? '').split('{privacy}');
      return {
        prefix: beforeTos ?? '',
        middle: middlePart ?? ' and ',
        suffix: afterPrivacy ?? '.',
      };
    });

    function onToggle(ev: Event): void {
      emit('update:checked', (ev.target as HTMLInputElement).checked);
    }
    function onTos(): void {
      // D-19:仅 emit,宿主决定跳转;组件不触发任何导航
      emit('agreement:tos-click');
    }
    function onPrivacy(): void {
      // D-19:仅 emit,宿主决定跳转;组件不触发任何导航
      emit('agreement:privacy-click');
    }

    return {
      tosText,
      privacyText,
      prefix: computed<string>(() => segments.value.prefix),
      middle: computed<string>(() => segments.value.middle),
      suffix: computed<string>(() => segments.value.suffix),
      onToggle,
      onTos,
      onPrivacy,
    };
  },
});
</script>

<style scoped>
.agreement {
  display: flex;
  align-items: flex-start;
  font-family: MiSans, Arial, sans-serif;
}

.checkbox-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
}

.native-cb {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  margin: 0;
  cursor: pointer;
}

.checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 2px solid #909499;
  border-radius: 4px;
  background: #FFFFFF;
}

.checkbox.checked {
  background: #F36F12;
  border-color: #F36F12;
}

.check-icon {
  width: 70%;
  height: 70%;
}

.text {
  color: #B6B6B6;
  flex: 1 1 auto;
  word-break: normal;
}

.link {
  color: #909499;
  text-decoration: underline;
  cursor: pointer;
}

.link:focus-visible {
  outline: 2px solid #2B78F5;
  outline-offset: 2px;
}

/* H5:容器 30×60 保证 tap target,本体 30×30;Figma 716:18911 实测 (默认 mobile-first) */
.checkbox-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
  width: 0.3rem;
  height: 0.4rem;
  margin-top: 0;
}

.checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 2px solid #909499;
  border-radius: 4px;
  background: #FFFFFF;
  width: 0.3rem;
  height: 0.3rem;
}

.text {
  color: #B6B6B6;
  flex: 1 1 auto;
  word-break: normal;
  margin-left: 0.16rem;
  font-size: 0.26rem;
  line-height: 1.5385;
}

/* PC: 大写 PX 用于跳过 postcss-pxtorem，避免协议区固定尺寸被转成 rem 后受根字号影响 */
.agreement.isPC .checkbox-wrapper {
  width: 24PX;
  height: 24PX;
  margin-top: 2PX;
}

.agreement.isPC .checkbox {
  width: 24PX;
  height: 24PX;
  border-width: 2PX;
  border-radius: 4PX;
}

.agreement.isPC .text {
  margin-left: 12PX;
  font-size: 18PX;
  line-height: 1.5;
}
</style>
